/**
 * Taxonomy registries for the programmatic /for pages.
 *
 * These are the controlled, DATA-DRIVEN vocabularies behind the use-case filter
 * and the adaptive "for your job / your industry" assembly. They are registries
 * (not bare label maps) so the system is:
 *   - EXTENSIVE — each role/industry declares which real APPS serve it, so the
 *     (product × audience) matrix grows straight from lib/apps-registry.ts. Add a
 *     product, tag it here once, and it flows into the filter and the adaptive view.
 *   - ADAPTIVE — a visitor's role/industry resolves to the relevant pages AND the
 *     relevant workspaces, composed on the fly. No page is minted per cell; the
 *     index assembles each visitor's view from the quality-gated page set.
 *
 * Rule (mirrors apps-registry / use-cases): `apps` must reference REAL APPS slugs.
 * The union types keep the filter chips consistent and typo-proof.
 */

/** Industry vertical a job serves. A use-case can span several. */
export type Industry =
  | "finance" | "legal" | "healthcare" | "education" | "real-estate"
  | "retail" | "media" | "travel" | "consulting" | "technology"
  | "government" | "nonprofit";

/** Primary role a page is written for — the canonical key behind `persona`. */
export type Role =
  | "analyst" | "deal-team" | "strategist" | "product" | "consultant"
  | "researcher" | "developer" | "founder" | "accountant" | "lawyer"
  | "clinician" | "trader" | "marketer" | "student" | "educator";

/** The dominant kind of work a job is. */
export type TaskType =
  | "research" | "analysis" | "diligence" | "monitoring"
  | "drafting" | "review" | "comparison" | "planning";

export type TaxonomyEntry<K extends string> = {
  key: K;
  /** Human label for the filter chip / eyebrow. */
  label: string;
  /** Real APPS slugs that serve this audience, in rough workflow order. */
  apps: string[];
};

/** Industry registry — key → label + the workspaces that serve that vertical. */
export const INDUSTRIES: TaxonomyEntry<Industry>[] = [
  { key: "finance",     label: "Finance",     apps: ["finance", "charts", "data", "research", "news"] },
  { key: "legal",       label: "Legal",       apps: ["legal", "research", "docs", "knowledge"] },
  { key: "healthcare",  label: "Healthcare",  apps: ["med", "research", "knowledge", "docs"] },
  { key: "education",   label: "Education",   apps: ["learn", "research", "knowledge", "docs"] },
  { key: "real-estate", label: "Real estate", apps: ["estate", "map", "finance", "docs"] },
  { key: "retail",      label: "Retail",      apps: ["shop", "data", "charts", "socials"] },
  { key: "media",       label: "Media",       apps: ["stream", "news", "socials", "create"] },
  { key: "travel",      label: "Travel",      apps: ["travel", "map", "research"] },
  { key: "consulting",  label: "Consulting",  apps: ["research", "docs", "charts", "knowledge"] },
  { key: "technology",  label: "Technology",  apps: ["code", "project", "workflow", "data"] },
  { key: "government",  label: "Government",  apps: ["research", "docs", "knowledge", "data"] },
  { key: "nonprofit",   label: "Nonprofit",   apps: ["docs", "project", "mail", "data"] },
];

/** Role registry — key → label + the workspaces that role runs. */
export const ROLES: TaxonomyEntry<Role>[] = [
  { key: "analyst",    label: "Analysts",    apps: ["research", "finance", "charts", "news", "data"] },
  { key: "deal-team",  label: "Deal teams",  apps: ["research", "finance", "docs", "storage"] },
  { key: "strategist", label: "Strategists", apps: ["research", "news", "charts", "knowledge"] },
  { key: "product",    label: "Product",     apps: ["project", "workflow", "code", "feedback"] },
  { key: "consultant", label: "Consultants", apps: ["research", "docs", "charts", "knowledge"] },
  { key: "researcher", label: "Researchers", apps: ["research", "knowledge", "docs", "search"] },
  { key: "developer",  label: "Developers",  apps: ["code", "project", "workflow", "docs"] },
  { key: "founder",    label: "Founders",    apps: ["dash", "finance", "project", "workflow"] },
  { key: "accountant", label: "Accountants", apps: ["finance", "data", "charts", "docs"] },
  { key: "lawyer",     label: "Legal",       apps: ["legal", "research", "docs", "knowledge"] },
  { key: "clinician",  label: "Clinicians",  apps: ["med", "research", "knowledge"] },
  { key: "trader",     label: "Traders",     apps: ["finance", "charts", "news", "data"] },
  { key: "marketer",   label: "Marketers",   apps: ["socials", "news", "create", "data"] },
  { key: "student",    label: "Students",    apps: ["learn", "research", "docs", "knowledge"] },
  { key: "educator",   label: "Educators",   apps: ["learn", "docs", "knowledge", "create"] },
];

/** Task-type registry — the dominant work pattern (no app binding needed). */
export const TASK_TYPES: TaxonomyEntry<TaskType>[] = [
  { key: "research",   label: "Research",   apps: [] },
  { key: "analysis",   label: "Analysis",   apps: [] },
  { key: "diligence",  label: "Diligence",  apps: [] },
  { key: "monitoring", label: "Monitoring", apps: [] },
  { key: "drafting",   label: "Drafting",   apps: [] },
  { key: "review",     label: "Review",     apps: [] },
  { key: "comparison", label: "Comparison", apps: [] },
  { key: "planning",   label: "Planning",   apps: [] },
];

// ── Lookups ──────────────────────────────────────────────────────────────────
export const INDUSTRY_LABELS: Record<Industry, string> =
  Object.fromEntries(INDUSTRIES.map((i) => [i.key, i.label])) as Record<Industry, string>;
export const ROLE_LABELS: Record<Role, string> =
  Object.fromEntries(ROLES.map((r) => [r.key, r.label])) as Record<Role, string>;
export const TASK_LABELS: Record<TaskType, string> =
  Object.fromEntries(TASK_TYPES.map((t) => [t.key, t.label])) as Record<TaskType, string>;
